import { DestroyRef, inject, Injectable } from '@angular/core';
import { Asset } from './asset.dto';
import { HttpService, ServiceEvent, ServiceEventType } from '@damofire/data-angular-commons';
import { Issue, Resolution } from '../issue/issue.dto';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { IssueCfgService } from '../config/issue-cfg.service';
import { logger, ASSETS_STORE, ChangeTyped, IndexedService, Query, SearchResult } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { IndexedQueryService } from '../indexed/indexed-query.service';
import { LockingIndexedService } from '../indexed/locking-indexed.service';
import { IssueService } from '../issue/issue.service';
import { AssetService, generateAndAssignAssetIds } from './asset.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class IdbAssetService extends AssetService {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    protected readonly issueService: IssueService,
    private readonly issueCfgService: IssueCfgService,
    private readonly lockingIdbService: LockingIndexedService,
    private readonly idbService: IndexedService,
    private readonly queryService: IndexedQueryService,
    protected readonly http: HttpService,
    protected readonly dtoSigner: AuthDtoSignerService,
    private readonly warehouseSyncEventService: WarehouseSyncEventService,
  ) {
    super(issueService, http, dtoSigner);
    // To replace the missing Cloud Functions when offline
    this.issueService
      .events()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.afterIssueWrite(event));
  }

  private static incrementIssueStats(currentAsset: Partial<Asset>, resolution: Resolution, count: number): void {
    if (resolution === Resolution.OPEN) {
      currentAsset.statistics.issues.unresolved += count;
    } else {
      currentAsset.statistics.issues.resolved += count;
    }
    currentAsset.statistics.issues.total += count;
  }

  private static hasToChangeStats(before: Issue, after: Issue): boolean {
    return before?.asset?._id !== after?.asset?._id || before.resolution !== after.resolution;
  }

  private static issueResolutionChanged(currentAsset: Partial<Asset>, beforeResolution: Resolution, afterResolution: Resolution): void {
    if (beforeResolution === Resolution.OPEN && afterResolution === Resolution.RESOLVED) {
      currentAsset.statistics.issues.unresolved--;
      currentAsset.statistics.issues.resolved++;
    } else if (beforeResolution === Resolution.RESOLVED && afterResolution === Resolution.OPEN) {
      currentAsset.statistics.issues.unresolved++;
      currentAsset.statistics.issues.resolved--;
    }
  }

  get(division: string, id: string | number): Promise<Asset> {
    return this.lockingIdbService.getByKey(division, ASSETS_STORE, [division, `${id}`]);
  }

  async search(divisionId: string, query: Query): Promise<SearchResult<Asset>> {
    return this.queryService.collection<Asset>(ASSETS_STORE, divisionId, query, ['clientCode', 'section', 'description']);
  }

  protected async dbUpdate(division: string, updated: Asset): Promise<void> {
    await this.dtoSigner.sign(updated, false, false);
    await this.idbService.update(ASSETS_STORE, updated).catch(console.error);
    logger.debug('AssetService - update - publishOutput', { id: updated._id, category: 'idb-asset' });
    this.warehouseSyncEventService.publishOutput({
      division,
      changes: [{ ...updated, changeType: 'modified' }],
      store: ASSETS_STORE,
    });
  }

  async create(divisionId: string, assets: Asset[]): Promise<Asset[]> {
    if (!assets.length) {
      return assets;
    }

    const assetWithIds: Asset[] = generateAndAssignAssetIds(assets);

    for (const asset of assetWithIds) {
      logger.info('Creating asset', { id: asset._id, category: 'idb-asset' });
      await this.dtoSigner.sign(asset, true);
      await this.idbService.add(ASSETS_STORE, asset);

      const changeTyped: ChangeTyped<Asset> = { ...asset, changeType: 'added' };
      this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: ASSETS_STORE });
      super.publishEvent(ServiceEventType.CREATED, asset);
    }

    return assetWithIds;
  }

  async delete(divisionId: string, assets: Asset[]): Promise<void> {
    if (!assets.length) {
      return;
    }

    const changes: ChangeTyped<Asset>[] = [];
    for (const asset of assets) {
      await this.idbService.delete(ASSETS_STORE, [divisionId, asset._id]);
      changes.push({ ...asset, changeType: 'removed' });
    }

    logger.info('Assets deleted', { count: assets.length, category: 'idb-asset' });
    super.publishEvent(ServiceEventType.DELETED, ...assets);
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes, store: ASSETS_STORE });
  }

  async updateBatch(assets: Asset[]): Promise<void> {
    if (!assets.length) {
      return;
    }

    for (const asset of assets) {
      try {
        asset.description = `S${asset.system} / U${asset.upright}`;
        await this.dbUpdate(asset.division?._id, asset);
      } catch (err) {
        logger.error('AssetService - update', { category: 'idb-asset', error: err });
      }
    }
  }

  private async afterIssueWrite(event: ServiceEvent<Issue>): Promise<void> {
    const assetMeta: { [id: string]: Partial<Asset> } = {};
    let division;

    for (const changes of event.items) {
      division = changes.before?.division?._id || changes.after?.division?._id;
      const beforeAssetId = changes.before?.asset?._id;
      const afterAssetId = changes.after?.asset?._id;
      const assetId = beforeAssetId || afterAssetId;
      const currentAsset = assetMeta[assetId] || {
        _id: assetId.toString(),
        riskLevel: { weight: 0 },
        statistics: { issues: { resolved: 0, total: 0, unresolved: 0 } },
      };

      if (IdbAssetService.hasToChangeStats(changes.before, changes.after)) {
        if (!beforeAssetId) {
          IdbAssetService.incrementIssueStats(currentAsset, changes.after.resolution, 1);
        } else if (!afterAssetId) {
          IdbAssetService.incrementIssueStats(currentAsset, changes.before.resolution, -1);
        } else {
          IdbAssetService.issueResolutionChanged(currentAsset, changes.before.resolution, changes.after.resolution);
        }
        assetMeta[assetId] = currentAsset;
      }

      const beforeWeight = IssueService.getEffectiveWeight(changes.before);
      const afterWeight = IssueService.getEffectiveWeight(changes.after);
      const newWeight = afterWeight - beforeWeight;
      if (newWeight) {
        currentAsset.riskLevel.weight += newWeight;
        assetMeta[assetId] = currentAsset;
      }
    }

    for (const unique of Object.values(assetMeta)) {
      await this.updateAssetMeta(division, unique);
    }
  }

  private async updateAssetMeta(division: string, assetMeta: Partial<Asset>): Promise<void> {
    const existingAsset = await this.get(division, assetMeta._id);
    const { resolved = 0, unresolved = 0, total = 0 } = existingAsset.statistics?.issues || {};
    const existingWeight = existingAsset.riskLevel?.weight || 0;
    const riskLevel = await this.issueCfgService.getIssuePriorityFromWeight(division, existingAsset.organization._id, existingWeight + assetMeta.riskLevel.weight);
    await this.update(
      division,
      {
        statistics: {
          issues: {
            resolved: resolved + assetMeta.statistics.issues.resolved,
            unresolved: unresolved + assetMeta.statistics.issues.unresolved,
            total: total + assetMeta.statistics.issues.total,
          },
        },
        riskLevel: { ...riskLevel, weight: existingWeight + assetMeta.riskLevel.weight },
      },
      [assetMeta._id],
    );
  }
}
